import { type CSSProperties, type DragEvent, type ReactNode } from 'react';
import {
    supportsDragPreview,
    suppressNativeDragPreview,
} from 'shared/drag_drop/suppressNativeDragPreview';

const DraggingOpacity = 0.25;

type DragSourceCallbacks = {
    onStart: (x: number, y: number) => void;
    onMove: (x: number, y: number) => void;
    onEnd: () => void;
};

type UseDragSourceHandlersOptions = DragSourceCallbacks & {
    isDragging: boolean;
    disabled?: boolean;
};

type DragSourceHandlers = {
    draggable: boolean;
    onDragStart: (e: DragEvent<HTMLElement>) => void;
    onDrag: (e: DragEvent<HTMLElement>) => void;
    onDragEnd: (e: DragEvent<HTMLElement>) => void;
    style: CSSProperties;
};

/**
 * Returns drag-source event handlers and style props. Use this when
 * drag source and drop target share the same DOM element (e.g.
 * TreeDragDrop).
 *
 * For standalone drag source wrapper element, use @see DragSource.
 */
export function useDragSourceHandlers({
    onStart,
    onMove,
    onEnd,
    isDragging,
    disabled = false,
}: UseDragSourceHandlersOptions): DragSourceHandlers {
    const handleDragStart = (e: DragEvent<HTMLElement>) => {
        if (disabled) {
            return;
        }
        onStart(e.pageX, e.pageY);
        suppressNativeDragPreview(e);
        e.dataTransfer.effectAllowed = 'move';
        e.stopPropagation();
    };

    const handleDrag = (e: DragEvent<HTMLElement>) => {
        if (supportsDragPreview && isDragging) {
            onMove(e.pageX, e.pageY);
        }
    };

    const handleDragEnd = (e: DragEvent<HTMLElement>) => {
        onEnd();
        e.preventDefault();
        e.stopPropagation();
    };

    return {
        draggable: !disabled,
        onDragStart: handleDragStart,
        onDrag: handleDrag,
        onDragEnd: handleDragEnd,
        style: {
            opacity: isDragging ? DraggingOpacity : undefined,
            cursor: disabled ? undefined : 'grab',
        },
    };
}

type DragSourceProps = DragSourceCallbacks & {
    isDragging: boolean;
    disabled?: boolean;
    children: ReactNode;
};

export function DragSource({
    onStart,
    onMove,
    onEnd,
    isDragging,
    disabled,
    children,
}: DragSourceProps) {
    const handlers = useDragSourceHandlers({
        onStart,
        onMove,
        onEnd,
        isDragging,
        disabled,
    });

    return <div {...handlers}>{children}</div>;
}
